/**
 * The colour maths the white label needs, and nothing else.
 *
 * It lives in `@lpa/protocol` because both ends need the *same* answers: the
 * API validates that a submitted palette is usable, and the browser derives the
 * live preview from it before anything is saved. Two implementations of
 * "does this clear AA" is two different apps.
 *
 * Everything works in sRGB hex and HSL. HSL rather than OKLCH deliberately:
 * the one operation this file exists to perform is "same colour, different
 * brightness" -- which is what `--accent` already does by hand between the
 * light and dark themes -- and holding hue and saturation while moving
 * lightness is exactly what HSL is for. OKLCH would be perceptually better
 * spaced and would also mean shipping a conversion matrix and its rounding
 * behaviour to a phone to save a few units of lightness.
 */

export interface Rgb {
  /** 0-255. */
  r: number;
  g: number;
  b: number;
}

export interface Hsl {
  /** Degrees, 0-360. */
  h: number;
  /** 0-1. */
  s: number;
  /** 0-1. */
  l: number;
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** True for `#abc` and `#aabbcc`, in either case. Nothing else is accepted. */
export function isHexColor(value: string): boolean {
  return HEX.test(value);
}

/** Normalises to lowercase six-digit `#rrggbb`. Throws on anything else. */
export function normalizeHex(value: string): string {
  const rgb = hexToRgb(value);
  return rgbToHex(rgb);
}

export function hexToRgb(value: string): Rgb {
  const match = HEX.exec(value);
  if (match === null) throw new Error(`not a hex colour: ${value}`);

  const digits = match[1] ?? '';
  // `#abc` is shorthand for `#aabbcc`, not for `#0a0b0c`.
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((d) => d + d)
          .join('')
      : digits;

  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

function clampByte(value: number): number {
  return Math.min(255, Math.max(0, Math.round(value)));
}

export function rgbToHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b].map((c) => clampByte(c).toString(16).padStart(2, '0')).join('')}`;
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const span = max - min;
  const l = (max + min) / 2;

  // Grey: hue is undefined, and 0 is as good a representative as any. Callers
  // that care check `s` first.
  if (span === 0) return { h: 0, s: 0, l };

  const s = l > 0.5 ? span / (2 - max - min) : span / (max + min);

  let h: number;
  if (max === rn) h = ((gn - bn) / span) % 6;
  else if (max === gn) h = (bn - rn) / span + 2;
  else h = (rn - gn) / span + 4;

  h *= 60;
  if (h < 0) h += 360;

  return { h, s, l };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));

  let rgb: [number, number, number];
  if (hp < 1) rgb = [c, x, 0];
  else if (hp < 2) rgb = [x, c, 0];
  else if (hp < 3) rgb = [0, c, x];
  else if (hp < 4) rgb = [0, x, c];
  else if (hp < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];

  const m = l - c / 2;
  return {
    r: clampByte((rgb[0] + m) * 255),
    g: clampByte((rgb[1] + m) * 255),
    b: clampByte((rgb[2] + m) * 255),
  };
}

export function hexToHsl(value: string): Hsl {
  return rgbToHsl(hexToRgb(value));
}

export function hslToHex(hsl: Hsl): string {
  return rgbToHex(hslToRgb(hsl));
}

/** WCAG relative luminance. */
export function luminance(value: string): number {
  const { r, g, b } = hexToRgb(value);

  const channel = (raw: number): number => {
    const c = raw / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1 to 21. Order of the arguments does not matter. */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Whichever of the two candidates is more readable on `background`. */
export function readableOn(background: string, dark = '#000000', light = '#ffffff'): string {
  return contrast(background, dark) >= contrast(background, light) ? dark : light;
}

/**
 * The nearest shade of `color` that clears `target` contrast against
 * `background`, holding hue and saturation.
 *
 * This is the operation the whole white label turns on. A client hands over one
 * brand colour; the app needs it as readable text on white *and* as readable
 * text on near-black, and neither is likely to be the hex they sent. Rather
 * than asking them for three, the lightness is walked away from the background
 * -- down on a light one, up on a dark one -- until the ratio is met.
 *
 * Walks in 1% steps from the original rather than binary-searching: the target
 * is monotonic in that direction, the range is 100 steps, and stepping means
 * the result is the *closest* qualifying shade rather than wherever a bisection
 * happened to land. If even pure black or pure white cannot reach the target
 * (a target above about 15:1 on mid-grey), the extreme is returned -- the best
 * available answer, and `contrast()` still tells the caller it fell short.
 */
export function shadeForContrast(color: string, background: string, target: number): string {
  const hsl = hexToHsl(color);
  if (contrast(hslToHex(hsl), background) >= target) return hslToHex(hsl);

  // Away from the background: darker text on a light surface, lighter on dark.
  const direction = luminance(background) > 0.5 ? -1 : 1;

  for (let step = 1; step <= 100; step++) {
    const l = Math.min(1, Math.max(0, hsl.l + (direction * step) / 100));
    const candidate = hslToHex({ ...hsl, l });

    if (contrast(candidate, background) >= target) return candidate;
    if (l === 0 || l === 1) break;
  }

  return hslToHex({ ...hsl, l: direction < 0 ? 0 : 1 });
}

/**
 * The smaller of the two ways round the colour wheel, 0-180.
 *
 * Hue is circular, so 350 and 10 are twenty degrees apart, not three hundred
 * and forty. Getting this wrong is what makes a naive "is this colour too close
 * to that one" check pass for two reds.
 */
export function hueDistance(a: number, b: number): number {
  const raw = Math.abs((((a - b) % 360) + 360) % 360);
  return raw > 180 ? 360 - raw : raw;
}
